import { useCallback, useLayoutEffect, useRef } from 'react';
import { VersesList } from '../verses/VersesList';
import type { VersesListProps } from '../verses/VersesList';
import { useLang } from '../i18n/lang';

/*
 * THE EXPANDED READER — a verse opened into the passage around it.
 *
 * The node drawer answers "where is this figure named"; this answers "what does the text actually say
 * there", by showing the opened verse with its neighbours up to the unit boundary. It reuses VersesList,
 * so a verse here reads exactly as it does in the drawer — original beside a named English, a second
 * translation, clickable names — and the same names remain doors: a click still travels the map.
 *
 * It opens ALREADY SCROLLED to the verse the reader clicked, with that verse marked, and there is no
 * "load more" button: reaching either end of the passage loads more of it, and the verse you were
 * reading holds its place on screen while it grows. Fewer clicks, less chrome, the whole passage still
 * reachable — see the "less UI" principle.
 */
export interface ReadingDrawerProps {
  /** The passage heading — the unit the verses belong to. */
  unit: string;
  /** The verse being read around, marked among its neighbours and scrolled to on open. */
  centerId: number | null;
  /** The loaded passage — the same shape VersesList takes anywhere else. */
  verses: VersesListProps;
  /** Grow the passage — called as the reader nears an end, with which side to extend. */
  onLoadMore: (direction: 'before' | 'after') => void;
  /** Whether there is more of the passage left to load above / below. */
  canLoadBefore: boolean;
  canLoadAfter: boolean;
  /** A companion of the opened passage — the other telling of a chapter told twice, or the other verse
   *  of a differing-number pair — shown as a slim ribbon; absent when the passage has none. */
  companion?: { label: string; onOpen: () => void } | null;
  /** Travel to another name a verse mentions. */
  onJump?: (id: string) => void;
  /** Re-centre the passage on a neighbouring verse (its citation is a control here too). */
  onExpand?: (id: number) => void;
  onClose: () => void;
}

/** How near an end (px) the reader loads more of the passage. */
const EDGE = 160;

export function ReadingDrawer(
  { unit, centerId, verses, onLoadMore, canLoadBefore, canLoadAfter, companion, onJump, onExpand, onClose }: ReadingDrawerProps,
): React.JSX.Element {
  const { t } = useLang();
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const positionedFor = useRef<number | null>(null); // the centre we have already scrolled to
  /* One load in flight at a time, and — for a load ABOVE — the scroll height before it, so the exact
     height prepended can be added back to scrollTop and the reader's view does not move at all. A load
     BELOW appends out of sight and needs no correction. */
  const loading = useRef<'before' | 'after' | null>(null);
  const heightBefore = useRef<number | null>(null);

  /*
   * Land on the opened verse, and hold the view still while more loads above. jsdom has no layout, so
   * these reads are all 0 there — harmless (nothing to scroll in a test); the behaviour is a browser's.
   *
   * THE NOTE OF HAVING DONE IT IS WRITTEN ONLY WHEN IT WAS DONE, and it used to be written either
   * way. That is NOT what broke this — the cause was one commit's lag in `useGrowOnScroll`, and
   * fixing it there fixes this with the line below in either form, which was checked by putting the
   * old one back and measuring again. It is kept in this form because the two faults compound: the
   * lookup below failed for a reason outside this file, and this memo is what made that failure
   * permanent rather than something the next render would put right.
   *
   * What it looked like, watched frame by frame in a real browser: at 159ms `.reader-body` is
   * mounted with `status: 'ready'` and NO verses in it. The effect looked exactly once, at 297ms,
   * found no `.verse--center`, scrolled nothing, and recorded the centre as positioned. `scrollTop`
   * stayed 0 through five seconds of watching, so "Open Matthew 27:5 on the map" put the reader
   * 1,351px above Matthew 27:5 — on every one of the 3,213 `?passage=` links the static pages carry.
   *
   * Retrying is bounded by construction: this runs only when `verses` or `centerId` changes, and a
   * centre that never renders simply never scrolls, exactly as before.
   */
  useLayoutEffect(() => {
    const body = bodyRef.current;
    if (!body || verses.status !== 'ready') return;
    if (centerId != null && positionedFor.current !== centerId) {
      const centre = body.querySelector<HTMLElement>('.verse--center');
      if (centre) {
        body.scrollTop = centre.offsetTop - body.clientHeight / 3; // a third down, in view
        positionedFor.current = centerId;
      }
    } else if (loading.current === 'before' && heightBefore.current !== null) {
      body.scrollTop += body.scrollHeight - heightBefore.current; // exactly cancel the prepend — no jump
    }
    loading.current = null;
    heightBefore.current = null;
  }, [verses, centerId]);

  const onScroll = useCallback((): void => {
    const body = bodyRef.current;
    if (!body || loading.current || verses.status !== 'ready') return;
    if (body.scrollTop <= EDGE && canLoadBefore) {
      loading.current = 'before';
      heightBefore.current = body.scrollHeight;
      onLoadMore('before');
    } else if (body.scrollTop + body.clientHeight >= body.scrollHeight - EDGE && canLoadAfter) {
      loading.current = 'after';
      onLoadMore('after');
    }
  }, [canLoadBefore, canLoadAfter, verses.status, onLoadMore]);

  return (
    <aside className="reader" role="region" aria-label={unit ? t('reader.label', { unit }) : t('reader.labelFallback')}>
      <div className="reader-head">
        <h2 className="reader-unit">{unit || t('reader.heading')}</h2>
        <button type="button" className="reader-close" onClick={onClose} aria-label={t('action.close')}>×</button>
      </div>
      {/* A companion sits ABOVE the scroll container, so it never shifts the passage's scroll maths. */}
      {companion && (
        <button type="button" className="reader-companion" onClick={companion.onOpen}>
          {companion.label} <span aria-hidden="true">→</span>
        </button>
      )}
      <div className="reader-body" ref={bodyRef} onScroll={onScroll}>
        <VersesList {...verses} onJump={onJump} onExpand={onExpand} highlightId={centerId ?? undefined} />
      </div>
    </aside>
  );
}
